import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, invalidateCsrf } from '../lib/api';
import { registerPush, unregisterPush } from '../lib/native';
import { quitarAvisos, sincronizarAvisos } from '../lib/avisosWeb';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.me().then(setUser).catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);

  const register      = useCallback((name, email, password, confirmPassword, fechaNacimiento) => api.register({ name, email, password, confirmPassword, fechaNacimiento }), []);
  const login         = useCallback(async (email, password) => { const d = await api.login(email, password); setUser(d.user); return d.user; }, []);
  const logout        = useCallback(async () => { await unregisterPush(); await quitarAvisos('usuario'); await api.logout().catch(() => {}); invalidateCsrf(); setUser(null); }, []);
  const updateProfile = useCallback(async (data) => { const u = await api.updateProfile(data); setUser(prev => ({ ...prev, ...u, profileComplete: true })); return u; }, []);

  // uploadPhoto recibe FormData directamente
  const uploadPhoto = useCallback(async (formData) => {
    const r = await api.uploadPhoto(formData);
    setUser(prev => ({ ...prev, foto_path: r.fotoPath, fotos: r.fotos || prev?.fotos || [] }));
    return r;
  }, []);

  const deletePhoto = useCallback(async (photoPath) => {
    const r = await api.deletePhoto(photoPath);
    setUser(prev => ({ ...prev, foto_path: r.fotoPath, fotos: r.fotos || [] }));
    return r;
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await api.me().catch(() => null);
    if (u) setUser(u);
  }, []);

  // Registrar el celular para las notificaciones push (app de Android/iOS, o la web si ya dio permiso)
  const userId = user?.id;
  useEffect(() => { if (userId) { registerPush(); sincronizarAvisos('usuario'); } }, [userId]);

  // Al volver a la pestaña o a la app, traer el perfil actualizado
  // (por si se editó desde otro dispositivo).
  useEffect(() => {
    if (!userId) return;
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 15000) return;
      last = Date.now();
      refreshUser();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [userId, refreshUser]);

  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout, updateProfile, uploadPhoto, deletePhoto, refreshUser }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
